import React from 'react';
import { CheckCircle2, User, Stethoscope, CalendarRange, Sparkles, Star } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import treatmentDetails from '../data/treatmentDetails';

/**
 * Detailed treatment explanation rendered after Overview.
 * Five sections in the same order as the reference site:
 *   1. What is [Treatment]?            (whatIs)
 *   2. Ideal Candidate                 (candidate)
 *   3. How the Procedure Is Performed  (procedure[])
 *   4. Recovery Timeline               (recovery[])
 *   5. Results & Why Gold Care Esthetic  (results + why)
 */
const TreatmentDetails = ({ slug, treatmentLabel }) => {
  const { t, lang } = useLang();
  const localized = treatmentDetails[slug];
  if (!localized) return null;
  const data = localized[lang] || localized.en;
  if (!data) return null;

  const procedureSteps = data.procedure || [];
  const recoveryMilestones = data.recovery || [];

  return (
    <section
      data-testid="treatment-details-section"
      className="bg-[#083325] text-[#ffffff] py-20 md:py-28"
    >
      <div className="max-w-[1100px] mx-auto px-6 space-y-16 md:space-y-20">

        {/* What is */}
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase mb-4">
              <Sparkles className="w-3 h-3" />
              {t('section.overview')}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              {t('td.whatIs')} <span className="peach-text italic">{treatmentLabel}?</span>
            </h2>
          </div>
          <p className="md:col-span-8 text-[17px] leading-relaxed text-white/80">
            {data.whatIs}
          </p>
        </div>

        <div className="h-px bg-[#D4AF37]/15" />

        {/* Ideal candidate */}
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase mb-4">
              <User className="w-3 h-3" />
              {t('td.candidate')}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              <span className="peach-text italic">{t('td.candidate')}</span>
            </h2>
          </div>
          <p className="md:col-span-8 text-[17px] leading-relaxed text-white/80">
            {data.candidate}
          </p>
        </div>

        <div className="h-px bg-[#D4AF37]/15" />

        {/* Procedure steps */}
        {procedureSteps.length > 0 && (
          <div>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase mb-4">
                <Stethoscope className="w-3 h-3" />
                {t('td.procedure')}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
                <span className="peach-text italic">{t('td.procedure')}</span>
              </h2>
            </div>
            <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {procedureSteps.map((step, i) => (
                <li
                  key={i}
                  data-testid={`td-procedure-step-${i}`}
                  className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors"
                >
                  <div className="font-serif-display text-4xl text-[#D4AF37] leading-none mb-3">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <p className="text-[15px] text-white/85 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="h-px bg-[#D4AF37]/15" />

        {/* Recovery timeline */}
        {recoveryMilestones.length > 0 && (
          <div>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase mb-4">
                <CalendarRange className="w-3 h-3" />
                {t('td.recovery')}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
                <span className="peach-text italic">{t('td.recovery')}</span>
              </h2>
            </div>
            <div className="relative grid md:grid-cols-4 gap-5">
              <div className="hidden md:block absolute top-6 left-[12%] right-[12%] h-px bg-[#D4AF37]/20 pointer-events-none" />
              {recoveryMilestones.map((milestone, i) => (
                <div
                  key={i}
                  data-testid={`td-recovery-step-${i}`}
                  className="relative bg-[#124D3A] border border-[#D4AF37]/15 rounded-2xl p-6 text-center"
                >
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#D4AF37] text-[#0B3D2E] font-bold flex items-center justify-center -mt-12 mb-4 shadow-lg ring-4 ring-[#083325]">
                    {i + 1}
                  </div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] mb-2">
                    {t(`td.timeline.${i}`)}
                  </div>
                  <p className="text-[14px] text-white/80 leading-relaxed">{milestone}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="h-px bg-[#D4AF37]/15" />

        {/* Results + Why Gold Care */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase mb-4">
              <CheckCircle2 className="w-3 h-3" />
              {t('td.results')}
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl mb-4">
              <span className="peach-text italic">{t('td.results')}</span>
            </h3>
            <p className="text-[16px] text-white/85 leading-relaxed">{data.results}</p>
          </div>

          <div className="bg-gradient-to-br from-[#D4AF37] to-[#A8832E] text-[#0B3D2E] rounded-3xl p-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-[#0B3D2E]/25 text-[#0B3D2E] text-[11px] tracking-[0.22em] uppercase mb-4">
              <Star className="w-3 h-3" />
              {t('td.why')}
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl mb-4 italic">
              {t('td.why')}
            </h3>
            <p className="text-[16px] text-[#0B3D2E]/90 leading-relaxed">{data.why}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TreatmentDetails;
